import React, { useEffect, Fragment, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { getNextPageUrl } from 'src/utils/frame';
import { useEventListQuery } from 'src/hooks/queries/eventListQuery';
import EventListPage from './EventListPage';
import { getBackfillEvents, getEvvntEvents } from 'src/helpers/eventListHelpers';
import SkeletonItems from 'src/components/organisms/events/SkeletonItems';
import Button from 'src/components/atoms/Button';
import { parsePage } from 'src/utils/pagination';
import IconHeader from 'src/components/molecules/IconHeader';
import { FaRegCalendar } from 'react-icons/fa';
import Text from 'src/models/Text';
import EventsGrid from '../../layout/EventsGrid';
import { updateEvPage } from 'src/utils/router';
import { useRouter } from 'src/hooks/useRouter';

const InfiniteScroll = ({ initialRawEvents, publisherSettings }) => {
  const text = new Text(publisherSettings);
  const router = useRouter();
  const initialPage = parsePage(router.query.evPage);
  const autoScrollDisabled = publisherSettings.publisher.calendar_auto_scroll === false;
  const [seeMoreEnabled, setSeeMoreEnabled] = useState(true);
  const {
    data,
    error,
    isFetchingNextPage,
    isFetchingPreviousPage,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
  } = useEventListQuery(initialRawEvents, initialPage);

  const loadNextButtonAction = (e) => {
    e.preventDefault();
    fetchNextPage();
    updateEvPage(router, lastPageNumber + 1);
  }

  const { ref } = useInView({
    rootMargin: '-25px 0px -25px 0px',
    trackVisibility: true,
    delay: 100,
    skip: autoScrollDisabled,
    onChange: (inView) => {
      if (inView) {
        fetchNextPage();
        updateEvPage(router, lastPageNumber + 1);
      }
    },
  });

  useEffect(() => {
    if (isFetchingNextPage) {
      setSeeMoreEnabled(false);
    } else {
      setSeeMoreEnabled(true);
    }
  }, [isFetchingNextPage]);

  const firstPageNumber = data?.pages[0]?.pageNumber;
  const lastPageNumber = data?.pages[data?.pages.length - 1]?.pageNumber;
  const evvntEventPages = data?.pages?.filter(
    (page) => !page.has_backfill_events || page.first_backfill_page
  );
  const hasEvvntEvents = getEvvntEvents(data?.pages[0]?.data).length > 0;
  const backfillPages = data?.pages?.filter((page) => page.has_backfill_events);

  const seePreviousBtn = () => {
    return (
      initialPage > 1 &&
      hasPreviousPage && (
        <div className="ml-4 inline-block z-50">
          <Button
            onClick={() => fetchPreviousPage()}
            extraClasses="text-sm w-full text-gray-500 justify-center"
            disabled={isFetchingPreviousPage}
            text={`You are on page ${firstPageNumber}. Click here to load previous`}
          />
        </div>
      )
    );
  };
  const basePageKey = `lp-${router.path}&query=${router.query.query}`;
  return (
    <>
      {hasEvvntEvents && (
        <>
          <IconHeader
            icon={<FaRegCalendar />}
            text={text.for('promoted_events_heading_text')}
          />
          {seePreviousBtn()}
          <EventsGrid>
            {evvntEventPages.map((page) => (
              <EventListPage
                key={`${basePageKey}-${page.pageNumber}`}
                events={getEvvntEvents(page.data)}
                pageNumber={page.pageNumber}
              />
            ))}
          </EventsGrid>
        </>
      )}
      {backfillPages.length > 0 && (
        <Fragment>
          <IconHeader icon={<FaRegCalendar />} text={text.for('other_events_heading_text')} />
          {!hasEvvntEvents && seePreviousBtn()}
          <EventsGrid>
            {backfillPages.map((page) => (
              <EventListPage
                key={page.pageNumber}
                events={getBackfillEvents(page.data)}
                pageNumber={page.pageNumber}
              />
            ))}
          </EventsGrid>
        </Fragment>
      )}
      {isFetchingNextPage ? <SkeletonItems count={8} /> : null}
      {error ? (
        <div className="flex flex-grow h-16 mb-16 items-center justify-center">
          <h2 className="font-semibold text-xl">Unable to load events</h2>
        </div>
      ) : null}
      {!isFetchingNextPage && hasNextPage && (
        <div ref={ref} id="sentinel" className="py-4">
          <a
            className={`${
              seeMoreEnabled ? '' : 'disabled opacity-50'
            } background-theme text-theme rounded p-4 font-semibold see-more-btn`}
            href={getNextPageUrl(lastPageNumber + 1)}
            onClick={loadNextButtonAction}
            title={`See more events on page ${lastPageNumber + 1}`}
          >
            See more events
          </a>
        </div>
      )}
    </>
  );
};

export default InfiniteScroll;
